<template>
  <b-overlay :show="processing" rounded="sm" class="overlay-display">
    <template #overlay>
      <div class="text-center">
        <b-spinner type="grow" variant="info" label="Loading..."></b-spinner>
        <p id="cancel-label">{{ processingMessage }}</p>
      </div>
    </template>
    <ErrorAlert
      :show="alert.error"
      :message="alert.message"
      @hide-alert="hideError"
    />
    <SuccessAlert
      :show="alert.success"
      :message="alert.message"
      @hide-alert="hideSuccess"
    />

    <div class="container-fluid p-0">
      <div class="row">
        <div class="col-auto">
          <h3><strong>Do we deliver to</strong></h3>
        </div>
      </div>

      <b-form id="form">
        <div>
          <b-tabs
            active-nav-item-class="font-weight-bold text-white bg-primary"
            v-model="activeTab"
            @input="onTabChanged"
            nav-class="font-weight-bold text-dark bg-secondary"
            justified
          >
            <b-tab title="Query postal code" active>
              <div class="row mt-3">
                <div class="col-sm">
                  <b-form-group>
                    <b-form-input
                      v-model="form.postalCode"
                      v-bind:readonly="isReadOnly"
                      :state="postalCodeState"
                      maxlength="7"
                      type="text"
                      placeholder="Please input Postal Code"
                      class="input-height mt-1"
                    ></b-form-input>
                  </b-form-group>
                </div>
                <div class="col-sm">
                  <b-form-group>
                    <label class="mt-0 report-label">&nbsp;</label>
                    <b-button
                      type="button"
                      variant="primary"
                      class="submit-button input-height mt-1"
                      @click="queryPostalCode"
                    >
                      Query Postal Code
                    </b-button>
                  </b-form-group>
                </div>
              </div>
            </b-tab>
            <b-tab title="Query town name">
              <div class="row mt-3">
                <div class="col-sm">
                  <b-form-group>
                    <v-select
                      :options="citiesOpt"
                      v-model="form.cityRegion"
                      v-bind:disabled="false"
                      @input="selectCity"
                      class="input-height mt-1"
                    >
                    </v-select>
                  </b-form-group>
                </div>
                <div class="col-sm">
                  <b-button
                    type="button"
                    variant="primary"
                    class="submit-button"
                    @click="selectCity"
                  >
                    Query Town
                  </b-button>
                </div>
              </div>

              <div class="row mt-3">
                <div class="col-sm">
                  <b-form-group>
                    <label class="mt-1 report-label">Postal Codes:</label>
                    <b-form-textarea
                      v-bind:readonly="true"
                      v-model="form.postalCodes"
                      rows="4"
                      max-rows="8"
                    ></b-form-textarea>
                  </b-form-group>
                </div>
              </div>
            </b-tab>
          </b-tabs>
        </div>

        <div class="row mt-3">
          <div class="col-sm">
            <b-form-group>
              <label class="mt-1 report-label">Result:</label>
              <b-form-input
                v-bind:readonly="true"
                v-model="form.information"
              ></b-form-input>
            </b-form-group>
          </div>
        </div>

        <div class="row mt-3">
          <div class="col-sm">
            <b-form-group>
              <label class="mt-1 report-label">Town:</label>
              <b-form-input
                v-bind:readonly="true"
                v-model="form.city"
                class="input-height mt-1"
              ></b-form-input>
            </b-form-group>
          </div>
          <div class="col-sm">
            <b-form-group>
              <label class="mt-1 report-label">Region:</label>
              <b-form-input
                v-bind:readonly="true"
                v-model="form.region"
                type="text"
                class="input-height mt-1"
              ></b-form-input>
            </b-form-group>
          </div>
        </div>
        <div class="row mt-3">
          <div class="col-sm">
            <b-form-group>
              <label class="mt-1 report-label">Cutoffs:</label>
              <b-form-textarea
                v-bind:readonly="true"
                v-model="form.cutoffs"
                rows="4"
                max-rows="12"
              ></b-form-textarea>
            </b-form-group>
          </div>
        </div>
        <div class="row mt-3">
          <div class="col-sm">
            <b-form-group>
              <label class="mt-1 report-label">Delivery date:</label>
              <b-form-datepicker
                :date-format-options="{
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit'
                }"
                v-model="form.deliveryDateNew"
                v-bind:disabled="isReadOnly"
                :date-disabled-fn="dateDisabled"
                @input="queryDeliveryWindow"
                class="mb-2"
                required
              ></b-form-datepicker>
            </b-form-group>
          </div>
        </div>
        <div class="row mt-3">
          <div class="col-sm">
            <b-form-group>
              <label class="mt-1 report-label">Delivery Window:</label>
              <b-form-textarea
                v-bind:readonly="true"
                v-model="form.deliveryRate"
                rows="4"
                max-rows="8"
              ></b-form-textarea>
            </b-form-group>
          </div>
        </div>
      </b-form>
    </div>
  </b-overlay>
</template>

<script>
import vSelect from "vue-select";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import advancedFormat from "dayjs/plugin/advancedFormat";
import customParseFormat from "dayjs/plugin/customParseFormat";
import weekOfYear from "dayjs/plugin/weekOfYear";
import isoWeek from "dayjs/plugin/isoWeek";
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);
dayjs.extend(weekOfYear);
dayjs.extend(isoWeek);

import ErrorAlert from "@/components/alerts/error.vue";
import SuccessAlert from "@/components/alerts/success.vue";

import FormatMixin from "@/mixin/format-mixin";
import { getAllCities, getCutoffs, getCutoffsByRegionId } from "@/utils/queryUtil";

export default {
  mixins: [FormatMixin],
  components: {
    ErrorAlert,
    SuccessAlert,
    "v-select": vSelect
  },
  computed: {
    postalCodeState() {
      return this.form.postalCode && this.form.postalCode.length >= 6
        ? true
        : false;
    }
  },
  data() {
    return {
      processing: false,
      processingMessage: "Please wait...",
      result: "",
      isReadOnly: false,
      activeTab: 0,
      regionId: "",
      form: {
        postalCode: "",
        deliveryDate: "",
        deliveryDateNew: "",
        information: "",
        deliveryRate: "",
        lang: "en-ca",
        cityRegion: "",
        city: "",
        region: "",
        postalCodes: "",
        cutoffs: ""
      },

      alert: {
        error: false,
        message: ""
      },

      citiesOpt: [],
      citiesObj: {},
      citiesPostalCodeObj: {},

      shippingMethodsOpt: [],
      closedDays: [],
      unavailableDates: [],
      availableDates: [],
      cutoffTime: [],

      weekdayObj: {
        "0": "Sunday",
        "1": "Monday",
        "2": "Tuesday",
        "3": "Wednesday",
        "4": "Thursday",
        "5": "Friday",
        "6": "Saturday"
      },
      effectObj: {
        "0": "Same-Day",
        "1": "Next-Day",
        "2": "Next-Day + 1",
        "3": "Next-Day + 2",
        "4": "Next-Day + 3"
      }
    };
  },
  async mounted() {
    this.getAllPostalCodes();
    this.queryCities();
  },
  methods: {
    scrollToTop() {
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    onTabChanged(newTabIndex) {
      this.clearForm();
    },
    async queryPostalCode() {
      if (!this.form.postalCode || this.form.postalCode.length < 6) {
        this.displayMessage("warning", `Postal Code can't be empty`);
        return false;
      }
      this.processing = true;
      this.displayMessage("Success", "Getting data...");

      try {
        const postalNew = this.form.postalCode.replace(/\s+/g, "");
        const queryUrl = `${process.env.VUE_APP_API_ADMIN_API_ENDPOINT}/places/postalcodes/query/${postalNew}`;
        const responseList = await this.axios
          .get(queryUrl)
          .then(res => {
            console.log("queryPostalCode response:", res.data);
            return res.data.result;
          })
          .catch(err => {
            console.log("error:", err);
            this.displayMessage("warning", `queryPostalCode Exception: ${err}`);
          });
        if (!responseList || responseList.length === 0) {
          this.form.information = "We Don't deliver there";
          this.clearForm();
          return;
        }
        this.form.information = "We deliver there";
        this.form.city = responseList[0].town_name;
        this.form.region = responseList[0].region_name;
        this.regionId = responseList[0].region_id;
        this.selectCity();
        // this.queryCutoffs();
        // console.log("queryPostalCode responseList:", responseList);
      } catch (e) {
        console.error("query error:", e);
      }

      this.processing = false;
    },

    async queryCutoffs() {
      this.displayMessage("Success", "Getting cutoffs...");
      // const region = this.form.region;
      const regionId = this.regionId;
      if (!regionId) {
        this.displayMessage("warning", `Please select region`);
        return;
      }
      const cutoffs = await getCutoffsByRegionId(regionId);
      if (cutoffs) {
        if (cutoffs.closedDates) {
          this.closedDays = cutoffs.closedDates.map(closeD => parseInt(closeD));
        }
        this.form.regionCode = cutoffs.region;
        this.unavailableDates = cutoffs.un_available;
        this.availableDates = cutoffs.available;
        this.cutoffTime = cutoffs.cutoff_time;
        // this.displayMessage("Success", "Cutoffs done...");
        let cutoffsText = "Cutoffs:\n";
        if (cutoffs.cutoff_time && cutoffs.cutoff_time.length > 0) {
          cutoffs.cutoff_time.sort((a, b) => {
            return a.day - b.day;
          });
          for (const df of cutoffs.cutoff_time) {
            cutoffsText += `${this.weekdayObj[df.day]}: ${
              this.effectObj[df.cutoff_effect]
            }, ${df.cutoff_time}\n`;
          }
        } else {
          cutoffsText = "No Cutoffs\n";
        }
        // cutoffsText += `Closed Days: ${this.closedDays.join(", ")}`;
        cutoffsText += `Unavailable Dates: ${this.unavailableDates.join(
          ", "
        )}\n`;
        cutoffsText += `Available Dates: ${this.availableDates
          .map(ad => ad.date)
          .join(", ")}`;
        this.form.cutoffs = cutoffsText;
        return true;
      } else {
        this.displayMessage("warning", `Query Cutoffs Exception`);
      }
    },
    async queryDeliveryWindow() {
      if (!this.form.postalCode) {
        return;
      }
      if (this.form.postalCode.length < 6) {
        return;
      }
      if (!this.form.deliveryDateNew) {
        return;
      }

      this.form.postalCode = this.form.postalCode.toUpperCase();
      await this.queryDeliveryRates(
        this.form.postalCode,
        this.form.deliveryDateNew,
        this.form.lang
      );
    },
    async queryDeliveryRates(postal, deliveryDate, lang) {
      const postalNew = postal.replace(/\s+/g, "");
      const rateUrl = `https://shop.callia.com/rates/delivery/postalcode/${postalNew}/date/${deliveryDate}?lang=${lang}`;
      const rateList = await this.axios
        .get(rateUrl)
        .then(res => {
          console.log("queryDeliveryRates response:", res.data);
          return JSON.parse(JSON.parse(res.data.body)).sort((a, b) => {
            return (
              Number(a.zone_rate.replace("$", "")) -
              Number(b.zone_rate.replace("$", ""))
            );
          });
        })
        .catch(err => {
          console.log("error:", err);
          this.displayMessage(
            "warning",
            `Query DeliveryRates Exception: ${err}`
          );
        });
      console.log("rateList:", rateList);
      this.form.deliveryRate = "";
      if (rateList) {
        for (const rate of rateList) {
          if (rate.status === true) {
            this.form.deliveryRate += `${rate.display_text} - ${rate.zone_rate}\n`;
          }
        }
      }
    },

    async queryCities() {
      const response = await getAllCities();
      if (response) {
        const allCitiesObj = response;
        const cities = [];
        for (const key of Object.keys(allCitiesObj)) {
          for (const val of allCitiesObj[key]) {
            for (const city of val.cities) {
              cities.push(`${city.name} - ${val.regionTag}`);
              const cityObj = {
                text: `${city.name}`,
                region: val.regionTag,
                regionId: val.regionId,
                cityId: city.id
              };
              this.citiesObj[city.name] = cityObj;
            }
          }
        }
        this.citiesOpt = cities.sort((a, b) => (a > b ? 1 : -1));
        // console.log("cities:", cities);
        // console.log("this.citiesOpt:", this.citiesOpt);
      } else {
        this.displayMessage("warning", `Query Cities Exception`);
      }
    },
    async getAllPostalCodes() {
      const response = await this.axios({
        method: "GET",
        url: `${process.env.VUE_APP_API_ADMIN_API_ENDPOINT}/places/postalcodes`,
        params: { offset: 0, limit: 10000 }
      });
      const postalCodes = response.data.result;

      this.citiesPostalCodeObj = {};
      postalCodes.reduce((acc, item) => {
        const key = item.town_name;
        if (!this.citiesPostalCodeObj[key]) {
          this.citiesPostalCodeObj[key] = [];
        }
        this.citiesPostalCodeObj[key].push(item.postalcode);
      }, {});

      if (this.form.city || this.form.cityRegion) {
        this.selectCity();
      }
    },
    dateDisabled(ymd, date) {
      const weekday = date.getDay();
      const dateSelected = dayjs.tz(ymd, "America/Winnipeg");
      const currentTimeStamp = dayjs().tz("America/Winnipeg");
      const today = currentTimeStamp.startOf("day");
      const formatedDate = dateSelected.format("YYYY-MM-DD");
      if (dateSelected.isBefore(today)) {
        return true;
      }
      if (this.availableDates && this.availableDates.length > 0) {
        for (const availableDates of this.availableDates) {
          if (availableDates.date.includes(formatedDate)) {
            const cutOffTimeStamp = dateSelected
              .subtract(parseInt(availableDates.cutoff_effect), "d")
              .startOf("d")
              .add(availableDates.cutoff_time.split(/:|\./)[0], "h")
              .add(availableDates.cutoff_time.split(/:|\./)[1], "m");

            if (cutOffTimeStamp.isAfter(currentTimeStamp)) {
              return false;
            }
          }
        }
      }
      if (this.unavailableDates.includes(formatedDate)) {
        return true;
      }
      if (this.closedDays.includes(weekday)) {
        return true;
      }

      const cutoff = this.cutoffTime.find(df => +df.day === dateSelected.day());
      //Calculate the cutoff time (d - cutoff_effect)
      if (cutoff) {
        const cutOffTimeStamp = dateSelected
          .subtract(parseInt(cutoff.cutoff_effect), "d")
          .startOf("d")
          .add(cutoff.cutoff_time.split(/:|\./)[0], "h")
          .add(cutoff.cutoff_time.split(/:|\./)[1], "m");

        return cutOffTimeStamp.isBefore(currentTimeStamp);
      } else {
        console.log("no cutoff", dateSelected.day());
        return true;
      }
    },
    async selectCity() {
      console.log("tab changed:", this.activeTab);
      console.log("selectCity:", this.form.city);
      this.form.region = "";
      let city;
      if (this.activeTab === 0) {
        city = this.form.city;
      } else {
        city = this.form.cityRegion.split(" - ")[0].trim();
      }
      if (city) {
        this.form.city = city;
        const cityObj = this.citiesObj[this.form.city];
        if (cityObj) {
          this.$set(this.form, "region", cityObj.region);
          this.regionId = cityObj.regionId;
          console.log("region:", this.form.region);
          if (this.form.region !== this.regionTemp) {
            await this.queryCutoffs();
            this.$set(this.form, "deliveryDateNew", null);
            this.form.deliveryDateNew = null;
            this.form.deliveryDateNew = "";
            // console.log("deliveryDateNew:", this.form.deliveryDateNew);
            this.$set(this.form, "deliveryDateNew", "");

            this.form.information = "We deliver there";

            // console.log("this.citiesPostalCodeObj:", this.citiesPostalCodeObj);
            const postalCodes = this.citiesPostalCodeObj[
              this.form.city
            ]?.sort((a, b) => (a > b ? 1 : -1));
            // console.log("postalCodes:", postalCodes);
            this.form.postalCodes = postalCodes?.join(", \n");
          }
        } else {
          this.displayMessage(
            "warning",
            `Region does not exist for city ${this.form.city} `
          );
          this.form.city = "";
        }
      } else {
        this.displayMessage("warning", `City is empty`);
      }
    },

    clearForm() {
      this.form = {
        postalCode: "",
        deliveryDate: "",
        deliveryDateNew: "",
        information: "",
        deliveryRate: "",
        lang: "en-ca",
        city: "",
        region: "",
        postalCodes: "",
        cutoffs: ""
      };
    },
    hideError() {
      this.alert.error = false;
    },
    hideSuccess() {
      this.alert.success = false;
    },
    displayMessage(messageType, messageBody) {
      this.$bvToast.toast("Empty Body", {
        title: messageBody,
        variant: messageType,
        headerClass: "py-2 px-4",
        bodyClass: "d-none",
        autoHideDelay: 5000,
        solid: true
      });
    },
    checkItems() {
      if (!this.form.postalCode) {
        this.displayMessage("warning", `Postal Code can't be empty`);
        return false;
      }
      return true;
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/_variables.scss";
@import "@/assets/scss/form.scss";
</style>
